/**
 * Dialog para generar link público de transparencia de un MediaReportRun.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Share2, Copy, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { media, type MediaReportRunOut } from "@/lib/api";

export function ReportShareDialog({ run }: { run: MediaReportRunOut }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [copied, setCopied] = useState(false);

  const share = useMutation({
    mutationFn: () => media.shareRun(run.id, expiresInDays),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "run", run.id] });
      qc.invalidateQueries({ queryKey: ["media", "reports"] });
      toast.success("Link público generado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const publicUrl = run.share_token
    ? `${window.location.origin}/public/reports/${run.share_token}`
    : null;

  function copy() {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Share2 size={14} /> Compartir
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link público de transparencia</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {publicUrl ? (
            <>
              <p className="text-xs text-ink-500">
                Este link es público — cualquier persona con el enlace puede descargar el reporte sin autenticación.
                Expira el {run.share_expires_at ? new Date(run.share_expires_at).toLocaleDateString("es-PE") : "—"}.
              </p>
              <div className="flex gap-2">
                <Input value={publicUrl} readOnly className="font-mono text-xs" />
                <Button onClick={copy} variant="outline" size="icon">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={() => share.mutate()} disabled={share.isPending}>
                Regenerar link
              </Button>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">Vigencia (días)</Label>
                <Input
                  type="number" min={1} max={365}
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                />
              </div>
              <p className="text-xs text-ink-500">
                Al generar, recibirá un token único accesible sin login. Útil para
                publicar en portal de transparencia institucional.
              </p>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cerrar</Button>
          {!publicUrl && (
            <Button onClick={() => share.mutate()} disabled={share.isPending}>
              Generar link público
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
